'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useRouter, useParams } from '@/shared/navigation'
import { enableHandoverSimulation } from '@/shared/env'
import {
  generateHandoverQr,
  isQrExpired,
} from '@/features/delman/api/generateHandoverQr'
import { simulateStoreReceive } from '@/features/delman/api/simulateStoreReceive'
import { DelmanPageHeader } from '@/features/delman/components/DelmanPageHeader'
import { useReceivingPoll } from '@/features/delman/hooks/useReceivingPoll'
import { delmanTerms } from '@/features/delman/delmanTerms'
import {
  saveTripRecord,
  type TripRecord,
} from '@/features/delman/storage/tripStorage'
import { loadTodayTripPreferApi } from '@/features/delman/api/delmanTripApi'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { QrDisplay } from '@/shared/components/QrDisplay'
import { Toast } from '@/shared/components/Toast'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'
import { qrCountdownLabel } from '@/shared/utils/qrCountdown'
import { buildDeliveryHandoverQrPayload } from '@/shared/utils/qrHandoverPayload'

export function DelmanHandoverPage() {
  const { deliveryId } = useParams<{ deliveryId: string }>()
  const router = useRouter()
  const hydrated = useClientHydrated()
  const device = hydrated ? getDelmanDevice() : null

  const [trip, setTrip] = useState<TripRecord | null>(null)
  const tripRef = useRef<TripRecord | null>(null)
  tripRef.current = trip

  const stop = trip?.stops.find((s) => s.id === deliveryId)

  const [token, setToken] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [now, setNow] = useState(0)
  const [autoRequested, setAutoRequested] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [isReceiving, setIsReceiving] = useState(false)
  const autoRegenForExpiry = useRef<string>('')

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  const expired = isQrExpired(expiresAt)
  const handoverOpen = stop?.receivingStatus === 'none'

  useEffect(() => {
    if (!hydrated || !device) return
    let cancelled = false
    void loadTodayTripPreferApi({
      delmanId: device.delmanId,
      delmanName: device.delmanName,
      kitchenName: device.kitchenName,
    }).then((loaded) => {
      if (!cancelled) setTrip(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [hydrated, device?.delmanId, device?.delmanName, device?.kitchenName])

  function persistTrip(next: TripRecord) {
    saveTripRecord(next)
    setTrip(next)
  }

  useReceivingPoll({
    enabled: hydrated && trip?.status === 'in_progress',
    getTrip: () => tripRef.current,
    onTripUpdate: persistTrip,
    onStoreScanned: (event) => {
      showToast(delmanTerms.handover.storeScannedToast(event.storeName))
    },
    onStoreCompleted: (event) => {
      showToast(delmanTerms.handover.storeCompletedToast(event.storeName))
    },
  })

  useEffect(() => {
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    if (!fullscreen) return undefined
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [fullscreen])

  const refreshQr = useCallback(async () => {
    if (!device || !deliveryId || !trip) return
    setIsLoading(true)
    try {
      const result = await generateHandoverQr({
        deliveryId,
        deviceId: device.deviceId,
      })
      const queued = consumeLastSyncQueued() ? ` ${delmanTerms.sync.queued}` : ''
      setToken(result.token)
      setExpiresAt(result.expires_at)

      const shownAt = new Date().toISOString()
      const nextStops = trip.stops.map((s) =>
        s.id === deliveryId
          ? {
              ...s,
              status: 'qr_shown' as const,
              qrToken: result.token,
              qrExpiresAt: result.expires_at,
              qrShownAt: shownAt,
            }
          : s,
      )
      persistTrip({ ...trip, stops: nextStops })
      showToast(`QR handover siap ditampilkan ke toko.${queued}`)
    } finally {
      setIsLoading(false)
    }
  }, [device, deliveryId, trip, showToast])

  useEffect(() => {
    if (!stop || handoverOpen) return
    setToken('')
    setExpiresAt('')
    setFullscreen(false)
  }, [stop, handoverOpen])

  useEffect(() => {
    if (!hydrated || !stop || autoRequested) return
    if (!handoverOpen) return
    setToken(stop.qrToken ?? '')
    setExpiresAt(stop.qrExpiresAt ?? '')
    const needsQr = !stop.qrToken || isQrExpired(stop.qrExpiresAt)
    if (needsQr) {
      setAutoRequested(true)
      void refreshQr()
    } else if (!stop.qrShownAt && trip && deliveryId) {
      const shownAt = new Date().toISOString()
      const nextStops = trip.stops.map((s) =>
        s.id === deliveryId ? { ...s, qrShownAt: shownAt } : s,
      )
      persistTrip({ ...trip, stops: nextStops })
    }
  }, [hydrated, stop, autoRequested, refreshQr, trip, deliveryId, handoverOpen])

  // Auto-regenerate QR saat masa berlaku habis (sekali per token kedaluwarsa,
  // dijaga ref agar tidak memanggil ulang terus jika regenerasi gagal).
  useEffect(() => {
    if (!hydrated || isLoading) return
    if (trip?.status !== 'in_progress') return
    if (!handoverOpen) return
    if (!token || !expired || !expiresAt) return
    if (autoRegenForExpiry.current === expiresAt) return
    autoRegenForExpiry.current = expiresAt
    void refreshQr()
  }, [hydrated, isLoading, trip?.status, handoverOpen, token, expired, expiresAt, refreshQr])

  async function handleSimulateReceive() {
    if (!token || !device || isReceiving || !trip) return
    setIsReceiving(true)
    try {
      await simulateStoreReceive(buildDeliveryHandoverQrPayload(deliveryId, token))
      setToken('')
      setExpiresAt('')
      setFullscreen(false)
      const nextStops = trip.stops.map((s) =>
        s.id === deliveryId
          ? {
              ...s,
              status: 'receiving_completed' as const,
              receivingStatus: 'completed' as const,
              lines: s.lines.map((line) => ({
                ...line,
                qtyReceived: line.qtyReceived ?? line.qtyShipped,
              })),
            }
          : s,
      )
      persistTrip({ ...trip, stops: nextStops })
      const loaded = await loadTodayTripPreferApi({
        delmanId: device.delmanId,
        delmanName: device.delmanName,
        kitchenName: device.kitchenName,
      })
      persistTrip(loaded)
      showToast('Seolah-olah toko sudah scan & terima barang.')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Simulasi terima gagal.')
    } finally {
      setIsReceiving(false)
    }
  }

  function simulateStoreScan() {
    if (!stop || !trip || !enableHandoverSimulation || !deliveryId) return
    const draftAt = new Date().toISOString()
    const nextStops = trip.stops.map((s) =>
      s.id === deliveryId
        ? {
            ...s,
            receivingStatus: 'draft' as const,
            status: 'receiving_draft' as const,
            receivingDraftAt: draftAt,
          }
        : s,
    )
    persistTrip({ ...trip, stops: nextStops })
    showToast(delmanTerms.handover.storeScannedToast(stop.storeName))
  }

  function ttlLabel(): string {
    return qrCountdownLabel(
      expiresAt,
      now,
      delmanTerms.handover.ttlExpired,
      delmanTerms.handover.ttlRemaining,
    )
  }

  if (!hydrated) {
    return <div className="min-h-[200px]" aria-hidden />
  }

  if (!stop) {
    return (
      <section className="card space-y-3">
        <p className="text-body-sm text-status-danger">{delmanTerms.delivery.notFound}</p>
        <Link href="/delman/trip" className="btn-outline block text-center">
          {delmanTerms.delivery.backToTrip}
        </Link>
      </section>
    )
  }

  const qrLabel = `${stop.storeName} · ${ttlLabel()}`
  const showQr = handoverOpen && !isLoading && Boolean(token) && !expired
  const awaitingScan = handoverOpen && stop.status === 'qr_shown' && stop.receivingStatus === 'none'
  const qrPayload = token ? buildDeliveryHandoverQrPayload(deliveryId, token) : ''

  return (
    <>
      <section className="space-y-4">
        <div className="card space-y-4">
          <DelmanPageHeader
            backHref={`/delman/delivery/${stop.id}`}
            backLabel={delmanTerms.delivery.backToTrip}
            meta={stop.id}
            title={delmanTerms.handover.title}
            subtitle={stop.storeName}
          />

          <p className="text-body-sm text-on-surface-variant text-center">{delmanTerms.handover.subtitle}</p>

          {awaitingScan && showQr && (
            <div className="flex items-center gap-2 rounded-lg bg-primary-container/40 border border-primary/30 px-3 py-2.5">
              <span className="material-symbols-outlined text-primary animate-pulse">qr_code_scanner</span>
              <p className="text-body-sm text-on-surface font-medium">{delmanTerms.handover.waitingScan}</p>
            </div>
          )}

          <div className="rounded-xl bg-inverse-surface text-inverse-on-surface p-4 space-y-4">
            {isLoading && (
              <p className="text-body-sm text-inverse-on-surface/70 text-center py-8">{delmanTerms.handover.loading}</p>
            )}

            {showQr && (
              <div className="py-2 space-y-3 text-inverse-on-surface/90">
                <QrDisplay token={qrPayload} label={qrLabel} />
                <button
                  type="button"
                  className="btn-outline w-full min-h-[48px] !text-inverse-on-surface !border-white/40 !bg-white/10"
                  onClick={() => setFullscreen(true)}
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined">fullscreen</span>
                    {delmanTerms.handover.fullscreen}
                  </span>
                </button>
              </div>
            )}

            {expired && !isLoading && (
              <div className="rounded-lg bg-status-warning-bg/20 border border-status-warning/40 px-3 py-4 text-center space-y-2">
                <p className="text-body-sm text-status-warning font-semibold">{delmanTerms.handover.expiredTitle}</p>
                <p className="text-label-md text-inverse-on-surface/70">{delmanTerms.handover.expiredHint}</p>
                <p className="font-mono text-body-sm text-inverse-on-surface/90">{ttlLabel()}</p>
              </div>
            )}

            {!isLoading && !token && !expired && (
              <p className="text-body-sm text-inverse-on-surface/70 text-center py-6">Tekan tombol di bawah untuk membuat QR.</p>
            )}
          </div>

          {stop.receivingStatus === 'completed' && (
            <div className="flex items-center gap-2 rounded-lg bg-status-success/10 border border-status-success/30 px-3 py-2">
              <span className="material-symbols-outlined text-status-success">check_circle</span>
              <p className="text-body-sm text-status-success font-semibold">
                {delmanTerms.handover.storeCompletedToast(stop.storeName)}
              </p>
            </div>
          )}

          {stop.receivingStatus === 'draft' && (
            <div className="flex items-center gap-2 rounded-lg bg-status-success/10 border border-status-success/30 px-3 py-2">
              <span className="material-symbols-outlined text-status-success">edit_note</span>
              <p className="text-body-sm text-status-success font-semibold">{delmanTerms.handover.storeDraft}</p>
            </div>
          )}

          <div className="space-y-2 pt-1">
            <p className="text-center font-mono text-label-md text-on-surface-variant">{ttlLabel()}</p>
            <button
              type="button"
              className="btn-primary w-full min-h-[52px] btn-tactile"
              disabled={isLoading}
              onClick={() => void refreshQr()}
            >
              {expired || !token ? delmanTerms.handover.create : delmanTerms.handover.refresh}
            </button>
            <button
              type="button"
              className="btn-outline w-full min-h-[44px]"
              onClick={() => router.push(`/delman/delivery/${stop.id}`)}
            >
              Lihat detail pengiriman
            </button>
          </div>

          {enableHandoverSimulation && showQr && stop.receivingStatus !== 'completed' && (
            <button
              type="button"
              className="btn-outline w-full min-h-[44px] !border-status-success/50 !text-status-success"
              disabled={isReceiving}
              onClick={() => void handleSimulateReceive()}
            >
              <span className="inline-flex items-center justify-center gap-2">
                <span className="material-symbols-outlined">qr_code_scanner</span>
                {isReceiving ? 'Memproses…' : 'Simulasi toko scan & terima'}
              </span>
            </button>
          )}

          {enableHandoverSimulation && (
            <button type="button" className="btn-outline w-full text-xs" onClick={simulateStoreScan}>
              [Dev] Simulasi toko scan QR (lokal)
            </button>
          )}
        </div>

        {toast && <Toast message={toast} onDismiss={dismissToast} />}
      </section>

      {fullscreen && showQr && (
        <div
          className="fixed inset-0 z-50 bg-inverse-surface flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label="QR handover layar penuh"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="min-w-0">
              <p className="text-body-sm font-semibold text-inverse-on-surface truncate">{stop.storeName}</p>
              <p className="font-mono text-label-md text-inverse-on-surface/60">{stop.id}</p>
            </div>
            <button
              type="button"
              className="shrink-0 min-h-[44px] min-w-[44px] rounded-lg text-inverse-on-surface/80 hover:text-inverse-on-surface flex items-center justify-center"
              onClick={() => setFullscreen(false)}
              aria-label={delmanTerms.handover.fullscreenClose}
            >
              <span className="material-symbols-outlined text-2xl">close</span>
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
            <QrDisplay token={qrPayload} label={qrLabel} size="large" />
            {awaitingScan && (
              <p className="text-body-sm text-primary-container font-semibold text-center animate-pulse">
                {delmanTerms.handover.waitingScan}
              </p>
            )}
            {stop.receivingStatus === 'draft' && (
              <p className="text-body-sm text-status-success font-semibold text-center">
                {delmanTerms.handover.storeDraft}
              </p>
            )}
            <p className="font-mono text-headline-md text-inverse-on-surface text-center">{ttlLabel()}</p>
          </div>
        </div>
      )}
    </>
  )
}
